import { useRef } from 'react'
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion'
import { ArrowLeft, Brain, Clapperboard, Play, Sparkles, Star } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { MiniMedal, MiniProjects, MiniStudents } from './mini-icons'
import { AuroraBackground } from '@/shared/motion/aurora-background'
import { ParticleField } from '@/shared/motion/particle-field'
import { Magnetic } from '@/shared/motion/magnetic'
import { KineticText } from '@/shared/motion/kinetic-text'
import { AnimatedNumber } from '@/shared/motion/animated-number'
import { useMouseSpring } from '@/shared/hooks/use-mouse-spring'
import { useDeferredMount } from '@/shared/hooks/use-deferred-mount'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { asset } from '@/shared/lib/asset'

const T = defineT({
  eyebrow: { ar: 'من الصفر للاحتراف', en: 'From Zero to Pro' },
  line1: { ar: 'أنا عبدالله عيسى', en: "I'm Abdallah Issa" },
  roles: { ar: 'مصمم ومطوّر ومتخصص', en: 'Designer, developer & specialist' },
  ai: { ar: 'في الذكاء الاصطناعي', en: 'in Artificial Intelligence' },
  description: {
    ar: 'خبرة أكثر من 10 سنوات في التصميم والبرمجة، وآخر سنتين تخصصت في تعليم استخدام الـ AI لتوليد الصور والفيديو والمحتوى الإبداعي بشكل احترافي.',
    en: '10+ years of design and engineering. The last two focused on teaching practical AI for images, video, and creative content.',
  },
  cta1: { ar: 'اكتشف خدماتي', en: 'Discover services' },
  cta2: { ar: 'شاهد الكورسات', en: 'Watch courses' },
  caption: { ar: 'متاح لمشاريع جديدة', en: 'Available for new projects' },
  badgeAi: { ar: 'AI · تصميم · كود', en: 'AI · Design · Code' },
  badgeYears: { ar: '10+ سنوات', en: '10+ years' },
  badgeRating: { ar: 'تقييم الطلاب', en: 'Student rating' },
  stat1Label: { ar: 'سنوات خبرة', en: 'Years experience' },
  stat2Label: { ar: 'طالب', en: 'Students' },
  stat3Label: { ar: 'مشروع', en: 'Projects' },
  float1: { ar: '10+ سنوات خبرة', en: '10+ years experience' },
  float2: { ar: 'متخصص ذكاء اصطناعي', en: 'AI specialist' },
  float3: { ar: 'صور وفيديو بالـ AI', en: 'AI images & video' },
  float4: { ar: '+1500 طالب بيتعلّم', en: '1500+ students taught' },
})

export function Hero() {
  const { t, locale, isRtl } = useTranslation(T)
  const sectionRef = useRef<HTMLElement>(null)
  const reducedMotion = useReducedMotion()
  const { xNorm, yNorm } = useMouseSpring()
  // Mount the canvas particle field + aurora pointer layer only after
  // the first paint so they don't steal main-thread time from the LCP.
  const showDecor = useDeferredMount()

  // Scroll-bound parallax — only the portrait floats; content stays put
  // so users don't feel like the page is fading on them.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, -60])
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 0.92])
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -30])
  const ratingY = useTransform(scrollYProgress, [0, 1], [0, 40])

  // Pointer-driven micro-tilt on the portrait
  const portraitTiltX = useTransform(xNorm, [-1, 1], [-10, 10])
  const portraitTiltY = useTransform(yNorm, [-1, 1], [-6, 6])

  const enLocale = locale === 'en' ? 'en-US' : 'ar-EG'

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-hidden pt-12 pb-20 sm:pt-20 sm:pb-28 lg:pt-28 lg:pb-32"
      aria-labelledby="hero-title"
    >
      {/* PRO cinematic backdrop (hero-bg-pro.webp) — replaces the old flat
          purple + buildings silhouette. Image sits at -z-20, a legibility veil
          at -z-10 keeps text readable in both themes, grid stays on top. */}
      <img
        src={asset('/hero-bg-pro.webp')}
        alt=""
        aria-hidden
        loading="eager"
        // @ts-ignore - fetchpriority lowercase needed for React 18 compat
        fetchpriority="high"
        className="pointer-events-none absolute inset-0 -z-20 h-full w-full object-cover"
        onError={(e) => {
          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
        }}
      />
      {/* Legibility veil — darkens edges, keeps center readable */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-background/85 via-background/55 to-background"
      />
      {/* Aurora mesh + particle field — deferred to idle so the hero
          text/portrait (LCP) paints before these canvas layers spin up
          their rAF loops. A lightweight static gradient holds the space
          until then so there's no visual pop. */}
      {showDecor ? (
        <>
          <AuroraBackground intensity={0.45} />
          <ParticleField />
        </>
      ) : (
        <div
          className="pointer-events-none absolute inset-0 -z-10 bg-mesh-gradient opacity-60"
          aria-hidden
        />
      )}

      {/* Floating PlayVeo-grade chips — glass pills drifting over the backdrop.
          Hidden on small screens + reduced-motion for perf. Pure CSS float. */}
      {!reducedMotion && (
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 hidden lg:block">
          <div className="hero-float hero-float-1">
            <Star className="h-3.5 w-3.5 fill-primary text-primary" />
            <span>{t('float1')}</span>
          </div>
          <div className="hero-float hero-float-2">
            <Brain className="h-3.5 w-3.5 text-primary" />
            <span>{t('float2')}</span>
          </div>
          <div className="hero-float hero-float-3">
            <Clapperboard className="h-3.5 w-3.5 text-primary" />
            <span>{t('float3')}</span>
          </div>
          <div className="hero-float hero-float-4">
            <Play className="h-3.5 w-3.5 text-primary" />
            <span>{t('float4')}</span>
          </div>
        </div>
      )}

      {/* Soft grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 grid-bg opacity-25"
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Asymmetric grid activates from md (768px) so the portrait
            is alongside the content on tablets too, not below the fold.
            On mobile (<md), portrait is on top and text is CENTERED. */}
        <div className="grid items-center gap-10 md:grid-cols-12 md:gap-8 lg:gap-12">
          {/* Content */}
          <div className="relative z-10 order-2 text-center md:order-1 md:col-span-7 md:text-start">
            {/* Eyebrow */}
            <motion.div
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <span className="eyebrow">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                {t('eyebrow')}
              </span>
            </motion.div>

            {/* Kinetic headline */}
            <h1
              id="hero-title"
              className="mt-6 font-display text-[2.5rem] font-extrabold leading-[1.28] tracking-tight sm:text-5xl sm:leading-[1.25] lg:text-6xl xl:text-7xl"
            >
              {/* overflow-hidden clips the word-reveal slide; the extra
                  py + -my keeps Arabic descenders (ص/ع/ى) and dots from
                  being cut while still masking the animation. */}
              <span className="block overflow-hidden py-[0.12em] -my-[0.12em]">
                <KineticText delay={0.04} stagger={0.05}>
                  {t('line1')}
                </KineticText>
              </span>
              <span className="mt-1 block overflow-hidden py-[0.12em] -my-[0.12em]">
                <KineticText delay={0.28} stagger={0.05} className="text-foreground/95">
                  {t('roles')}
                </KineticText>
              </span>

              {/* AI highlight — its own designed line. No overflow clip;
                  reveals via opacity/blur so the gradient-clipped Arabic
                  isn't cut. Animated gradient + glow + underline sweep. */}
              <motion.span
                initial={
                  reducedMotion ? false : { opacity: 0, y: 14, filter: 'blur(8px)' }
                }
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.7, delay: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative mt-2 inline-flex w-fit flex-col"
              >
                {/* Soft glow puddle behind the phrase */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-x-4 -inset-y-2 -z-10 rounded-full bg-primary/20 blur-2xl"
                />
                <span className="text-gradient-animated inline-block pb-[0.18em] leading-[1.35]">
                  {t('ai')}
                </span>
                {/* Animated underline sweep */}
                {!reducedMotion && (
                  <motion.span
                    aria-hidden
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{
                      duration: 0.7,
                      delay: 0.95,
                      ease: [0.2, 0.8, 0.2, 1],
                    }}
                    style={{ transformOrigin: isRtl ? 'right' : 'left' }}
                    className="mt-1 h-1 w-full rounded-full bg-gradient-to-r from-primary via-primary-300 to-primary-600"
                  />
                )}
              </motion.span>
            </h1>

            <motion.p
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85, ease: [0.2, 0.8, 0.2, 1] }}
              className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg md:mx-0"
            >
              {t('description')}
            </motion.p>

            <motion.div
              initial={reducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0, ease: [0.2, 0.8, 0.2, 1] }}
              className="mt-9 flex flex-wrap items-center justify-center gap-3 md:justify-start"
            >
              <Magnetic>
                <Button size="lg" className="group" onClick={() => (window.location.href = '/studio')}>
                  <Clapperboard className="h-4 w-4" aria-hidden />
                  {locale==='ar' ? 'جرّب الاستوديو' : 'Try Studio'}
                  <ArrowLeft
                    className={cn(
                      'h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5',
                      !isRtl && 'rotate-180 group-hover:translate-x-0.5',
                    )}
                    aria-hidden
                  />
                </Button>
              </Magnetic>
              <Magnetic>
                <Button size="lg" variant="ghost" className="group">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/15 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    <Play className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  {t('cta2')}
                </Button>
              </Magnetic>
            </motion.div>

            <motion.p
              initial={reducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.15 }}
              className="mt-7 inline-flex items-center justify-center gap-2 text-sm text-muted-foreground"
            >
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              </span>
              {t('caption')}
            </motion.p>
          </div>

          {/* Portrait */}
          <div className="relative order-1 md:order-2 md:col-span-5">
            <motion.div
              style={
                reducedMotion ? undefined : { y: portraitY, scale: portraitScale }
              }
              className="relative mx-auto aspect-[4/5] w-full max-w-[18rem] sm:max-w-sm md:max-w-none"
            >
              {/* Layered halos */}
              <motion.div
                aria-hidden
                className="absolute -inset-10 rounded-[40%] bg-gradient-to-tr from-primary/55 via-accent/30 to-transparent blur-3xl"
                animate={
                  reducedMotion
                    ? undefined
                    : { scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }
                }
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Photo card */}
              <motion.div
                style={
                  reducedMotion
                    ? undefined
                    : {
                        rotateX: portraitTiltY,
                        rotateY: portraitTiltX,
                        transformPerspective: 1200,
                      }
                }
                initial={reducedMotion ? false : { opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative h-full w-full overflow-hidden rounded-[2rem] border border-primary/20 shadow-2xl shadow-primary/30"
              >
                <img
                  src={asset('/hero-home.webp')}
                  alt="Abdallah Issa portrait"
                  width={520}
                  height={650}
                  className="h-full w-full object-cover"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background/40 to-transparent"
                  aria-hidden
                />

                {/* Sweeping highlight */}
                {!reducedMotion && (
                  <motion.div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/12 to-transparent"
                    initial={{ x: '-150%' }}
                    animate={{ x: '150%' }}
                    transition={{
                      duration: 2.4,
                      ease: 'easeInOut',
                      repeat: Infinity,
                      repeatDelay: 6,
                    }}
                  />
                )}
              </motion.div>

              {/* Floating badge — bottom start */}
              <motion.div
                style={reducedMotion ? undefined : { y: badgeY }}
                initial={
                  reducedMotion ? false : { opacity: 0, y: 24, scale: 0.9 }
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 1.0, ease: [0.2, 0.8, 0.2, 1] }}
                className="absolute -bottom-5 -start-3 sm:-start-6 rounded-2xl border border-primary/40 bg-card/95 p-3 sm:p-4 shadow-2xl shadow-black/40 backdrop-blur-xl"
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground shadow-glow-sm">
                    <Brain className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="text-start">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {t('badgeAi')}
                    </p>
                    <p className="font-display text-sm font-bold text-foreground">
                      {t('badgeYears')}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating rating badge — top end */}
              <motion.div
                style={reducedMotion ? undefined : { y: ratingY }}
                initial={reducedMotion ? false : { opacity: 0, x: 20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 1.2, ease: [0.2, 0.8, 0.2, 1] }}
                className="absolute -top-3 -end-2 rounded-2xl border border-border bg-card/95 px-3 py-2 shadow-2xl shadow-black/40 backdrop-blur-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="flex" aria-hidden>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-3 w-3 fill-primary text-primary"
                      />
                    ))}
                  </div>
                  <span className="font-display text-sm font-bold tabular-nums">
                    4.9
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t('badgeRating')}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Stats — animated counters with mini brand icons + PRO hover */}
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.6 }}
          className="relative mt-16 hidden grid-cols-3 gap-3 rounded-3xl border border-border/60 bg-card/50 p-5 backdrop-blur-md sm:mt-20 sm:gap-8 sm:p-8"
        >
          <div className="absolute inset-x-8 -top-px h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          {[
            { v: 10, suf: '+', l: t('stat1Label'), Icon: MiniMedal },
            { v: 1500, suf: '+', l: t('stat2Label'), Icon: MiniStudents },
            { v: 200, suf: '+', l: t('stat3Label'), Icon: MiniProjects },
          ].map((s, i) => (
            <div
              key={i}
              className="stat-hover group flex flex-col items-center gap-2 rounded-2xl px-2 py-3 text-center sm:flex-row sm:items-center sm:justify-center sm:gap-3"
            >
              <span className="mini-icon grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
                <s.Icon className="h-5 w-5" aria-hidden />
              </span>
              <span className="flex flex-col items-center sm:items-start">
                <span className="stat-number">
                  <AnimatedNumber value={s.v} suffix={s.suf} locale={enLocale} />
                </span>
                <span className="text-xs text-muted-foreground transition-colors duration-300 group-hover:text-foreground sm:text-sm">
                  {s.l}
                </span>
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
